import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useWindowWidth } from '@elfsight/lib-utils';
import {
  getVisibilityOnPageSettings,
  isVisibleOnDevice,
  isVisibleOnPage,
  useDisplayInSchedule
} from '@elfsight/widget-conditional-display';
import { getRememberVisitorValue, useStorage } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { isStopDisplayAvailable } from './utils/get-stop-display-available-type';

const barStateContext = createContext<{
  isOpen: boolean;
  closeBar: () => void;
  stopDisplayTrigger: (type: StopDisplayTriggerTypes) => void;
} | null>(null);

interface BarStateProps extends PropsWithChildren {
  editMode?: boolean;
}

export enum StopDisplayTriggerTypes {
  CLOSEDBAR = 'closedBar',
  CLICKEDBAR = 'clickedBar',
  ACTIONELEMENT = 'actionElement',
  VIEWLIMIT = 'viewLimit'
}

const SECONDS_VIEW_COUNTER_DISABLED = 3;

export const BarStateProvider = ({ children }: BarStateProps) => {
  const [isOpen, setOpen] = useState<boolean>(false);

  const [
    stopDisplayStorageValue,
    { set: setStopDisplay, isExpired: checkStopDisplayExpired }
  ] = useStorage<StopDisplayTriggerTypes | null>('bar-stop-display', null);

  const [
    ,
    { set: setBarView, get: getBarViews, getTimestamp: getBarViewTimestamp }
  ] = useStorage<number>('bar-views-count', 0);

  const settings = useSettings();
  const {
    stopDisplayDuration,
    stopDisplayViewsLimit,
    stopDisplayReachedViewLimit,
    editMode,
    visibilityOnDevices,
    schedule
  } = settings;

  const stopDisplayTrigger = (type: StopDisplayTriggerTypes) => {
    const availableType = isStopDisplayAvailable(type, settings);
    setStopDisplay(availableType);
  };

  const closeBarHandler = () => {
    setOpen(false);
    if (editMode) {
      setTimeout(() => setOpen(true), 750);
    }
  };

  const windowWidth = useWindowWidth();

  // TODO: сделать то же самое тока внутри isVisible in Page
  const visibleOnPagesSettings = getVisibilityOnPageSettings(settings);

  const scheduleVisibility = useDisplayInSchedule({
    settings,
    enabled: schedule
  });
  const onPageVisibility = isVisibleOnPage(visibleOnPagesSettings);
  const deviceVisibility = isVisibleOnDevice(visibilityOnDevices, windowWidth);

  const stopDisplayExpiredIn = getRememberVisitorValue(stopDisplayDuration);
  const isStopDisplayExpired = checkStopDisplayExpired(stopDisplayExpiredIn);

  useEffect(() => {
    if (isStopDisplayExpired) {
      setStopDisplay(null);
    }
  }, [isStopDisplayExpired]);

  useEffect(() => {
    if (editMode) {
      setOpen(true);
      return;
    }

    const stopDisplayValue = isStopDisplayExpired
      ? null
      : isStopDisplayAvailable(stopDisplayStorageValue, settings);

    const isVisible =
      scheduleVisibility &&
      onPageVisibility &&
      deviceVisibility &&
      !stopDisplayValue;

    setOpen(isVisible);
  }, [
    stopDisplayStorageValue,
    deviceVisibility,
    scheduleVisibility,
    onPageVisibility,
    editMode,
    isStopDisplayExpired
  ]);

  useEffect(() => {
    // из-за того, что компонент рендерится несколько раз, мы накладываем временные
    // ограничения на увеличение счетчика появления бара в 3 секунды
    const shouldSkip =
      !stopDisplayReachedViewLimit ||
      stopDisplayStorageValue ||
      !scheduleVisibility ||
      !onPageVisibility ||
      !deviceVisibility;

    if (shouldSkip) {
      return;
    }

    const views = getBarViews() ?? 0;
    if (views === 0) {
      setBarView(1);
      return;
    }

    const isLimitReached = views + 1 > stopDisplayViewsLimit;
    const now = Math.floor(Date.now() / 1000);
    const isUnderViewCooldown =
      now <= getBarViewTimestamp() + SECONDS_VIEW_COUNTER_DISABLED;

    if (isUnderViewCooldown) {
      return;
    }

    if (!isLimitReached) {
      setBarView(views + 1);
      return;
    }

    stopDisplayTrigger(StopDisplayTriggerTypes.VIEWLIMIT);
    setOpen(false);
    setBarView(0);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stopDisplayStorageValue,
    isOpen,
    stopDisplayStorageValue,
    stopDisplayReachedViewLimit,
    scheduleVisibility,
    onPageVisibility,
    deviceVisibility
  ]);

  return (
    <barStateContext.Provider
      value={{ isOpen, closeBar: closeBarHandler, stopDisplayTrigger }}
    >
      {children}
    </barStateContext.Provider>
  );
};

export function useBarState() {
  const context = useContext(barStateContext);

  if (context === null) {
    throw new Error(`useBarState should be used within StateContext`);
  }

  return context;
}
